import React, {useEffect, useState} from 'react';
import {menu, logs} from "../translate";
import Api from "../Api";
import {
  Box,
  Fade,
  Modal,
  Typography,
  Grid,
  Paper,
  Divider,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CloseIcon from "@mui/icons-material/Close";
import { greenTheme } from "../helpers/style";

const customStyles = {
    menu: (provided) => ({
        ...provided,
        position: 'absolute',
        zIndex: 9999,
        maxHeight: 300,
    }),
    menuPortal: (base) => ({...base, zIndex: 9999}), // Ensures portal menu is above modal
};
const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: window.innerWidth > 1200 ? 1200 : window.innerWidth - 40,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    maxHeight: window.innerHeight > 565 ? '85vh' : '95vh',
    overflow: 'auto',
    borderRadius: 2,
};

/** ✅ Safe deep compare helper (no JSON.stringify pitfalls) */
function deepEqual(a, b, seen = new WeakMap()) {
  if (Object.is(a, b)) return true;

  if (a === null || b === null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;

  // Cycle protection
  const seenA = seen.get(a);
  if (seenA && seenA === b) return true;
  seen.set(a, b);

  // Date
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }

  // RegExp
  if (a instanceof RegExp || b instanceof RegExp) {
    return a instanceof RegExp && b instanceof RegExp && a.source === b.source && a.flags === b.flags;
  }

  // Arrays
  const aIsArr = Array.isArray(a);
  const bIsArr = Array.isArray(b);
  if (aIsArr || bIsArr) {
    if (!(aIsArr && bIsArr)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i], seen)) return false;
    }
    return true;
  }

  // Different prototypes (Map vs Object etc.)
  if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
  }

  for (const k of aKeys) {
    if (!deepEqual(a[k], b[k], seen)) return false;
  }

  return true;
}

function LogDetail({open, setOpen}) {
    const [single, setSingle] = useState({})
    const [before, setBefore] = useState(null)
    const [loading, setLoading] = useState(false)
    const langIndex = +localStorage.getItem('atsLang') || 1
    
    useEffect(() => {
        if (open) {
            (async () => {
                setLoading(true)
                try {
                    const {data} = await Api.getSingleLogs(open)
                    setSingle(data.logs)
                    setBefore(data.before || null)
                } catch (e) {
                    console.error('Error fetching log details:', e)
                } finally {
                    setLoading(false)
                }
            })()
        }
    }, [open])

    const parseData = (dataString) => {
        if (!dataString) return null
        try {
            return typeof dataString === 'string' ? JSON.parse(dataString) : dataString
        } catch (e) {
            return null
        }
    }

    const formatValue = (value) => {
        if (value === null || value === undefined) return '-'
        if (typeof value === 'boolean') return value ? 'Yes' : 'No'
        if (typeof value === 'object' && Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : 'Empty array'
        }
        return String(value)
    }

    const renderValue = (value) => {
        if (value === null || value === undefined) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    -
                </Typography>
            )
        }
        
        if (typeof value === 'boolean') {
            return (
                <Typography variant="body2">
                    {value ? 'Yes' : 'No'}
                </Typography>
            )
        }
        
        if (Array.isArray(value)) {
            return (
                <Box>
                    {value.length > 0 ? (
                        value.map((item, idx) => (
                            <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                                • {formatValue(item)}
                            </Typography>
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            Empty array
                        </Typography>
                    )}
                </Box>
            )
        }
        
        if (typeof value === 'object') {
            return (
                <Box sx={{ pl: 1 }}>
                    {Object.entries(value).map(([subKey, subValue]) => (
                        <Box key={subKey} sx={{ mb: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    display: 'block',
                                    mb: 0.25,
                                    textTransform: 'capitalize',
                                }}
                            >
                                {subKey.replace(/_/g, ' ')}:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    ml: 2,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {formatValue(subValue)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )
        }
        
        return (
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {String(value)}
            </Typography>
        )
    }

    const renderDataFields = (data) => {
        if (!data || typeof data !== 'object') return null

        return Object.entries(data).map(([key, value]) => (
            <Box key={key} sx={{ mb: 2 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: greenTheme.primary,
                        textTransform: 'capitalize',
                    }}
                >
                    {key.replace(/_/g, ' ')}
                </Typography>
                <Paper
                    sx={{
                        p: 2,
                        backgroundColor: '#fafafa',
                        border: `1px solid ${greenTheme.primary}`,
                        borderRadius: 1,
                    }}
                >
                    {typeof value === 'object' && !Array.isArray(value) && value !== null ? (
                        <Box sx={{ pl: 1 }}>
                            {Object.entries(value).map(([subKey, subValue]) => (
                                <Box key={subKey} sx={{ mb: 1.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            display: 'block',
                                            mb: 0.5,
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        {subKey.replace(/_/g, ' ')}:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            ml: 2,
                                            wordBreak: 'break-word',
                                            fontFamily: typeof subValue === 'number' || typeof subValue === 'string' ? 'inherit' : 'monospace',
                                        }}
                                    >
                                        {formatValue(subValue)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                wordBreak: 'break-word',
                                fontFamily: typeof value === 'number' || typeof value === 'string' ? 'inherit' : 'monospace',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {formatValue(value)}
                        </Typography>
                    )}
                </Paper>
            </Box>
        ))
    }

    /** ✅ Only compare keys that exist in BOTH before and after */
    const getChanges = (beforeData, afterData) => {
        if (!beforeData || !afterData) return []

        const changes = []

        // intersection: before ∩ after
        const keys = Object.keys(beforeData).filter((k) =>
            Object.prototype.hasOwnProperty.call(afterData, k)
        )

        keys.forEach((key) => {
            const beforeValue = beforeData[key]
            const afterValue = afterData[key]

            // ✅ deep compare (safe)
            if (!deepEqual(beforeValue, afterValue)) {
                changes.push({
                    field: key,
                    oldValue: beforeValue,
                    newValue: afterValue,
                })
            }
        })

        return changes
    }

    const getMethodColor = (method) => {
        switch (method) {
            case 'POST':
                return 'success'
            case 'PUT':
                return 'warning'
            case 'DELETE':
                return 'error'
            case 'GET':
                return 'info'
            default:
                return 'default'
        }
    }

    const parsedData = parseData(single?.data)
    const hasBeforeAfter = parsedData?.before && parsedData?.after
    const isPutMethod = single.method === 'PUT'

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={!!open}
            onClose={() => setOpen(0)}
            closeAfterTransition
            slots={{backdrop: Backdrop}}
            slotProps={{
                backdrop: {
                    timeout: 500,
                },
            }}
        >
            <Fade in={!!open}>
                <Box sx={style}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {menu.logs[langIndex] || 'Log Details'}
                        </Typography>
                        <IconButton
                            onClick={() => setOpen(0)}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: greenTheme.primary }} />
                        </Box>
                    ) : (
                        <>
                            {/* Metadata */}
                            <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
                                <Grid container spacing={2}>
                                    {single.method && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                {logs.method?.[langIndex] || 'Method'}
                                            </Typography>
                                            <Chip
                                                label={logs['method' + single.method]?.[langIndex] || single.method}
                                                color={getMethodColor(single.method)}
                                                size="small"
                                            />
                                        </Grid>
                                    )}
                                    {single.admin && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                {logs.admin?.[langIndex] || 'Admin'}
                                            </Typography>
                                            <Typography variant="body2">{single.admin}</Typography>
                                        </Grid>
                                    )}
                                    {single.date && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                {logs.date?.[langIndex] || 'Date'}
                                            </Typography>
                                            <Typography variant="body2">
                                                {new Date(single.date).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {single.ip && (
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                IP
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                {single.ip}
                                            </Typography>
                                        </Grid>
                                    )}
                                
                                </Grid>
                            </Paper>

                            <Divider sx={{ mb: 3 }} />

                            {/* Data Display */}
                            {(isPutMethod && hasBeforeAfter) ? (
                                (() => {
                                    const changes = getChanges(parsedData.before, parsedData.after)
                                    return changes.length > 0 ? (
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                                Changes
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {changes.map((change, index) => (
                                                    <Paper
                                                        key={index}
                                                        sx={{
                                                            p: 2.5,
                                                            border: `2px solid ${greenTheme.primary}`,
                                                            borderRadius: 2,
                                                            backgroundColor: '#fafafa',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: 700,
                                                                mb: 2,
                                                                color: greenTheme.primary,
                                                                textTransform: 'capitalize',
                                                            }}
                                                        >
                                                            {change.field.replace(/_/g, ' ')}
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <Box
                                                                    sx={{
                                                                        p: 2,
                                                                        borderRadius: 1,
                                                                        backgroundColor: '#fff3e0',
                                                                        border: '1px solid #ffcc80',
                                                                        minHeight: '60px',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            display: 'block',
                                                                            mb: 1.5,
                                                                            fontWeight: 600,
                                                                            color: '#e65100',
                                                                        }}
                                                                    >
                                                                        Old Value:
                                                                    </Typography>
                                                                    {renderValue(change.oldValue)}
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Box
                                                                    sx={{
                                                                        p: 2,
                                                                        borderRadius: 1,
                                                                        backgroundColor: '#f1f8e9',
                                                                        border: `1px solid ${greenTheme.primary}`,
                                                                        minHeight: '60px',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            display: 'block',
                                                                            mb: 1.5,
                                                                            fontWeight: 600,
                                                                            color: greenTheme.primary,
                                                                        }}
                                                                    >
                                                                        New Value:
                                                                    </Typography>
                                                                    {renderValue(change.newValue)}
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Paper>
                                                ))}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Paper
                                            sx={{
                                                p: 3,
                                                textAlign: 'center',
                                                backgroundColor: '#f5f5f5',
                                            }}
                                        >
                                            <Typography variant="body1" color="text.secondary">
                                                No changes detected
                                            </Typography>
                                        </Paper>
                                    )
                                })()
                            ) : (
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: greenTheme.primary,
                                        }}
                                    >
                                        {logs.data?.[langIndex] || 'Data'}
                                    </Typography>
                                    {parsedData ? (
                                        <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>
                                            {renderDataFields(parsedData)}
                                        </Box>
                                    ) : (
                                        <Paper
                                            sx={{
                                                p: 3,
                                                textAlign: 'center',
                                                backgroundColor: '#f5f5f5',
                                            }}
                                        >
                                            <Typography variant="body1" color="text.secondary">
                                                No data available
                                            </Typography>
                                        </Paper>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
}

export default LogDetail;
